/**
 * AdminMembershipSync — read-only + retry view over native membership
 * rows' Systeme contact/tag sync health.
 *
 * Every row is retried automatically every 5 minutes by the backend
 * sweep loop (membership.systeme_sync_retry_sweep_loop) whenever
 * systeme_sync_status is "pending" — this page exists purely so an
 * admin can SEE a stuck sync (with the exact error and attempt count)
 * and force an immediate retry instead of waiting.
 */
import { useEffect, useState } from "react";
import { RefreshCw, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";

function relativeTime(iso) {
  if (!iso) return "never";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function AdminMembershipSync() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/api/membership/admin/systeme-sync-status");
      setRows(data.subscriptions || []);
      setError("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const retry = async (row) => {
    const key = `${row.email}:${row.plan_id}`;
    setRetrying((r) => ({ ...r, [key]: true }));
    try {
      await api.post(`/api/membership/admin/systeme-sync-status/${encodeURIComponent(row.email)}/${row.plan_id}/retry`);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRetrying((r) => ({ ...r, [key]: false }));
    }
  };

  const pending = (rows || []).filter((r) => r.systeme_sync_status !== "synced");
  const synced = (rows || []).filter((r) => r.systeme_sync_status === "synced");

  return (
    <div data-testid="admin-membership-sync-page">
      <PageHero
        eyebrow="JCC Admin"
        title="Membership → Systeme sync"
        description="Every native Stripe membership and whether it's mirrored to Systeme (contact + tag). Payment and entitlement are never affected by a sync failure — this is a CRM mirror, retried automatically every 5 minutes."
      />
      <section className="section pt-8">
        <div className="container-jcc max-w-5xl">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 mb-6" data-testid="admin-membership-sync-error">
              {error}
            </div>
          )}
          {rows === null && !error && <p className="text-ink-muted">Loading…</p>}

          {rows !== null && (
            <>
              {pending.length > 0 && (
                <div className="mb-8">
                  <h2 className="font-display font-semibold text-lg flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-5 h-5" /> Needs attention ({pending.length})
                  </h2>
                  <div className="mt-3 space-y-3">
                    {pending.map((row) => {
                      const key = `${row.email}:${row.plan_id}`;
                      return (
                        <div
                          key={key}
                          className="rounded-xl border border-amber-200 bg-amber-50 p-4"
                          data-testid={`admin-membership-sync-row-${row.email}`}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="font-semibold text-ink">{row.email}</p>
                              <p className="text-xs text-ink-soft mt-0.5">
                                {row.plan_id} · membership status: {row.status} · {row.systeme_sync_attempts || 0} attempt(s) ·
                                last tried {relativeTime(row.systeme_sync_last_attempted_at)}
                              </p>
                              {row.systeme_sync_last_error && (
                                <p className="text-sm text-amber-800 mt-2" data-testid={`admin-membership-sync-error-${row.email}`}>
                                  {row.systeme_sync_last_error}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => retry(row)}
                              disabled={retrying[key]}
                              className="btn-outline shrink-0"
                              data-testid={`admin-membership-sync-retry-${row.email}`}
                            >
                              <RefreshCw className={`w-4 h-4 ${retrying[key] ? "animate-spin" : ""}`} />
                              {retrying[key] ? "Retrying…" : "Retry now"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <h2 className="font-display font-semibold text-lg flex items-center gap-2 text-ink">
                  <CheckCircle2 className="w-5 h-5 text-teal" /> Synced ({synced.length})
                </h2>
                {synced.length === 0 ? (
                  <p className="mt-2 text-sm text-ink-muted">None yet.</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {synced.map((row) => (
                      <div
                        key={`${row.email}:${row.plan_id}`}
                        className="rounded-xl border border-slate-200 bg-white p-3.5 flex items-center justify-between text-sm"
                      >
                        <span className="text-ink">{row.email} <span className="text-ink-soft">· {row.plan_id}</span></span>
                        <span className="text-ink-soft flex items-center gap-1.5 text-xs">
                          <Clock className="w-3.5 h-3.5" /> synced {relativeTime(row.systeme_sync_synced_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {rows.length === 0 && (
                <p className="text-ink-muted" data-testid="admin-membership-sync-empty">
                  No native memberships yet.
                </p>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
